var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo, useCallback } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { FuzzyHighlighter, Highlighter } from '@citrus/highlighter';
import { getAutomation, getDebug, getStyleNames, useCitrusDebug } from '@citrus/runtime';
import { useTheme } from '@citrus/user';
import { isNotNilOrEmpty, isString, removeKey } from '@pcty/utils';
import { CitronInput, Checkbox as Input, InputWrapper, Label, LabelSingle, LabelSingleWrapper, InnerWrapper as XInnerWrapper, InnerWrapperChecked as XInnerWrapperChecked, TextWrapper, } from './Checkbox.styled';
import { textWrapperDisabled, error as errorLegacy, disabledHighlight as disabledHighlightLegacy, } from '../styles/legacy';
import { error as errorCitron, textWrapperDisabled as textWrapperDisabledCitron, disabledHighlight as disabledHighlightCitron, } from '../styles/citron';
import { useCheckbox } from './private/CheckboxContext';
var VERSION = '7.3.1';
var Checkbox = React.forwardRef(function (_a, ref) {
    var _b, _c, _d;
    var automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, disabled = _a.disabled, _e = _a.error, error = _e === void 0 ? false : _e, _f = _a.highlighter, highlighter = _f === void 0 ? 'fuzzy' : _f, id = _a.id, index = _a.index, _g = _a.onChange, onChange = _g === void 0 ? function () { return null; } : _g, _h = _a.selected, selected = _h === void 0 ? false : _h, value = _a.value, _j = _a.__TYPE, __TYPE = _j === void 0 ? 'Checkbox' : _j, other = __rest(_a, ["automationId", "children", "className", "css", "disabled", "error", "highlighter", "id", "index", "onChange", "selected", "value", "__TYPE"]);
    var debug = useCitrusDebug();
    var isCitron = useTheme().citrus2022;
    var context = useCheckbox();
    var classes = cx((_b = {},
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b), className || '');
    var classesInput = cx((_c = {},
        _c[emotionCss(errorLegacy)] = !isCitron && error,
        _c[emotionCss(errorCitron)] = isCitron && error,
        _c));
    var checkboxListMode = !!context;
    var Component = checkboxListMode ? Label : LabelSingle;
    var processLabelWrapperClick = function (e) {
        if (context) {
            // for lists, raise _onChange() for the icon (which is displayed for citron when checked)
            if (isCitron && e.target.tagName === 'I') {
                _onChange();
            }
        }
    };
    var _onChange = function () {
        if (context) {
            context.onChange({ id: context === null || context === void 0 ? void 0 : context.groupId, index: index !== null && index !== void 0 ? index : 0, selected: !selected });
            return;
        }
        onChange({ id: (id !== null && id !== void 0 ? id : 0).toString(), index: 0, selected: !selected });
    };
    var textWrapperEmotionObj = (_d = {},
        _d[emotionCss(textWrapperDisabled)] = !isCitron && (disabled || (context === null || context === void 0 ? void 0 : context.disabled)),
        _d[emotionCss(textWrapperDisabledCitron)] = isCitron && (disabled || (context === null || context === void 0 ? void 0 : context.disabled)),
        _d);
    var textWrapperClasses = cx(textWrapperEmotionObj);
    var textWrapperStyleNames = {
        textWrapperDisabled: Boolean(textWrapperEmotionObj[emotionCss(textWrapperDisabled)] ||
            textWrapperEmotionObj[emotionCss(textWrapperDisabledCitron)]),
    };
    var getHighlightable = useCallback(function (highlighter, disabled, content) {
        if (highlighter === 'disabled')
            return content;
        var disabledHighlight = isCitron ? disabledHighlightCitron : disabledHighlightLegacy;
        if (highlighter === 'fuzzy' && isString(content)) {
            return React.createElement(FuzzyHighlighter, { css: disabled ? disabledHighlight : undefined, text: content });
        }
        return React.createElement(Highlighter, { css: disabled ? disabledHighlight : undefined }, content);
    }, [isCitron]);
    var text = useMemo(function () { var _a, _b; return getHighlightable((_a = context === null || context === void 0 ? void 0 : context.highlighter) !== null && _a !== void 0 ? _a : highlighter, (_b = (disabled || (context === null || context === void 0 ? void 0 : context.disabled))) !== null && _b !== void 0 ? _b : false, children); }, [getHighlightable, context === null || context === void 0 ? void 0 : context.highlighter, context === null || context === void 0 ? void 0 : context.disabled, highlighter, disabled, children]);
    var InnerWrapper = selected ? XInnerWrapperChecked : XInnerWrapper;
    var getComponent = function () { return (React.createElement(Component, __assign({}, (context && { ariaLabelledBy: "".concat(children, "-for-labelledby") }), { className: classes, columns: context === null || context === void 0 ? void 0 : context.columns, ref: ref }, getDebug(debug, __TYPE, VERSION)),
        React.createElement(InnerWrapper, null,
            React.createElement(InputWrapper, { onClick: processLabelWrapperClick },
                React.createElement(Input, __assign({ "aria-checked": selected, checked: selected, className: classesInput, disabled: disabled || (context === null || context === void 0 ? void 0 : context.disabled), label: children, onChange: _onChange, required: other === null || other === void 0 ? void 0 : other.required, type: "checkbox" }, (isNotNilOrEmpty(context) ? { name: context === null || context === void 0 ? void 0 : context.groupId } : {}), (value && { value: value }), getAutomation(automationId), removeKey(other, 'required'))),
                React.createElement(CitronInput, { disabled: disabled || (context === null || context === void 0 ? void 0 : context.disabled), selected: selected, visible: isCitron })),
            React.createElement(TextWrapper, __assign({}, (context && { id: "".concat(children, "-for-labelledby") }), { className: textWrapperClasses, styleNames: getStyleNames(textWrapperStyleNames) }), text)))); };
    return checkboxListMode ? getComponent() : React.createElement(LabelSingleWrapper, null, getComponent());
});
Checkbox.defaultProps = {
    __TYPE: 'Checkbox',
};
export default Checkbox;
